/* FD brand ---------------------------------------------------------------
   Delivery-zone postcode checker: the wiring between the form and the cards.

   It builds no copy. Every result card is already in the section's markup with
   its wording coming from theme settings; this file only decides which one is
   unhidden. That is deliberate — a string built here would be a customer-facing
   sentence nobody can edit in the theme editor.

   Zone routing:
   - window.FDZones.lookup(postcode) -> { zone: 1–6, district } or null.
   - Zone 1 -> the "included" card. Zone 2 -> the flat-rate card. Zones 3, 4, 5
     and 6 -> the one shared "get in touch for a quote" card (ruling
     2026-08-03: we deliver everywhere, so no zone is a dead end). null -> the
     try-again card.
   - The resolved district is deliberately unused. The lookup resolves zones,
     it does not validate postcodes, so echoing the district back would imply
     we had recognised an address we may not have.

   Motion: the CSS animations sit inside a prefers-reduced-motion:no-preference
   block and switch themselves off. The confetti is a canvas, which no media
   query can stop, so it carries its own matchMedia check here. Its colours are
   read from the live --fd-* custom properties rather than written in, so it can
   never drift from the palette; if none resolve, the burst is simply skipped.

   With JS off the form still submits and the page reloads: no answer, but
   nothing broken and nothing claimed.
   ------------------------------------------------------------------------ */
(function () {
  'use strict';

  var CONFETTI_TOKENS = ['--fd-green', '--fd-tan', '--fd-tan-soft', '--fd-paper'];
  var confettiFrame = null;

  function motionAllowed() {
    return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // The OUTWARD half of what was typed, for the "For B1" caption. Never the
  // resolved district: the lookup is not a validator, and echoing what it
  // resolved would read as "we recognise this address". The inward code is
  // dropped only when the tail actually looks like one — a digit then two
  // letters — and the whole is longer than four characters, so a four-character
  // outward code typed alone (LS11, SW1A) survives intact. Same rule as
  // fd-zone-lookup.js.
  function outwardCode(value) {
    var s = String(value || '').replace(/\s+/g, '').toUpperCase();
    return s.length > 4 && /\d[A-Z]{2}$/.test(s) ? s.slice(0, -3) : s;
  }

  function setEcho(root, code) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-fd-pc-echo]'), function (echo) {
      var slot = echo.querySelector('[data-fd-pc-echo-code]');
      if (slot) slot.textContent = code;
      echo.hidden = !code;
    });
  }

  function reset(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-fd-pc-card]'), function (card) {
      card.removeAttribute('data-fd-pc-shown');
      card.hidden = true;
    });
    setEcho(root, '');
  }

  function cardKeyFor(result) {
    if (!result) return 'none';
    if (result.zone === 1) return '1';
    if (result.zone === 2) return '2';
    return '3';
  }

  function show(root, key) {
    var shown = null;
    var cards = root.querySelectorAll('[data-fd-pc-card]');

    Array.prototype.forEach.call(cards, function (card) {
      var match = card.getAttribute('data-fd-pc-card') === key;
      // Cleared first so re-checking the same postcode replays the animation:
      // an attribute that never changes never restarts one.
      card.removeAttribute('data-fd-pc-shown');
      card.hidden = !match;
      if (match) shown = card;
    });

    if (!shown) return null;

    // Force a reflow between the removal above and the flag below, or the
    // browser coalesces the two and the animation is skipped.
    void shown.offsetWidth;
    shown.setAttribute('data-fd-pc-shown', '');
    return shown;
  }

  function confettiColours(el) {
    var styles = window.getComputedStyle(el);
    var colours = [];

    CONFETTI_TOKENS.forEach(function (token) {
      var value = styles.getPropertyValue(token).trim();
      if (value) colours.push(value);
    });

    return colours;
  }

  function confettiBurst(fromEl) {
    var colours = confettiColours(fromEl);
    if (!colours.length) return;

    var old = document.querySelector('.fd-pc__confetti');
    if (old) {
      if (confettiFrame) window.cancelAnimationFrame(confettiFrame);
      old.remove();
    }

    var canvas = document.createElement('canvas');
    canvas.className = 'fd-pc__confetti';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);

    var width = window.innerWidth;
    var height = window.innerHeight;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    var ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.remove();
      return;
    }
    ctx.scale(dpr, dpr);

    var box = fromEl.getBoundingClientRect();
    var originX = box.left + box.width / 2;
    var originY = box.top + 6;
    var pieces = [];
    var i;

    for (i = 0; i < 80; i++) {
      var angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
      var speed = 7 + Math.random() * 8;
      pieces.push({
        x: originX + (Math.random() - 0.5) * box.width * 0.7,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        w: 5 + Math.random() * 5,
        h: 8 + Math.random() * 6,
        colour: colours[i % colours.length],
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.35,
        life: 90 + Math.random() * 50,
      });
    }

    var frames = 0;

    function tick() {
      ctx.clearRect(0, 0, width, height);
      var alive = 0;

      pieces.forEach(function (piece) {
        if (piece.life <= 0) return;
        alive++;
        piece.vy += 0.28;
        piece.vx *= 0.985;
        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.rot += piece.vr;
        piece.life--;

        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate(piece.rot);
        ctx.globalAlpha = Math.min(1, piece.life / 30);
        ctx.fillStyle = piece.colour;
        ctx.fillRect(
          -piece.w / 2,
          -piece.h / 2,
          piece.w,
          piece.h * (0.4 + 0.6 * Math.abs(Math.sin(piece.rot * 2)))
        );
        ctx.restore();
      });

      frames++;
      if (alive > 0 && frames < 220) {
        confettiFrame = window.requestAnimationFrame(tick);
      } else {
        confettiFrame = null;
        canvas.remove();
      }
    }

    tick();
  }

  function init(root) {
    var form = root.querySelector('[data-fd-pc-form]');
    var input = root.querySelector('[data-fd-pc-input]');
    var region = root.querySelector('[data-fd-pc-result]');
    if (!form || !input || !region) return;

    // The moment the postcode changes, the old answer goes: whatever is on
    // screen must belong to the postcode in the box (2026-09-06 user test --
    // a second postcode left the first answer standing and read as its own).
    input.addEventListener('input', function () { reset(root); });

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      var lookup = window.FDZones && window.FDZones.lookup;
      if (!lookup) return;

      var key = cardKeyFor(lookup(input.value));
      var card = show(root, key);
      if (!card) return;

      setEcho(root, key === 'none' ? '' : outwardCode(input.value));

      region.focus();

      if (key === '1' && motionAllowed()) confettiBurst(card);
    });
  }

  function start() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-fd-postcode]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The theme editor rebuilds a section's markup wholesale on edit.
  document.addEventListener('shopify:section:load', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll('[data-fd-postcode]'), init);
  });
})();
